<template>
  <div class="panel-action">
    <button
      v-if="isPastStatus"
      class="button-primary primary-normal small w-75 mt-2"
      @click="handleOpenProduct"
    >
      {{ t('reservations.proceedToAvailability') }}
    </button>
    <a
      v-else
      @click.prevent="handlePopupLink"
      href="#"
      class="link-default d-flex"
    >
      <span class="link-text">
        {{ t('reservations.viewQuote') }}
        <ChevronLogo class="chevron-icon mb-1" />
      </span>
    </a>
  </div>
</template>

<script setup lang="ts">
  /**
   * Script setup for the Panel Action component
   * Handles conditional rendering and link/button actions based on booking status.
   */

  // Vue core imports
  import { ComputedRef, computed, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'

  // TypeScript types
  import { BookingSummary } from '@/services/BookingService.interface'

  // Configurations and enums
  import { PrestType } from '@/config'

  // Local assets
  import ChevronLogo from '@/assets/ChevronRight.vue'

  // Vuex store
  import { useStore } from '@/store'
  const store = useStore()

  // Composables
  import { useDomainRedirect } from '@/composables/useDomainRedirect'
  const { handleRedirect } = useDomainRedirect()

  // Define props with proper typing
  const props = defineProps({
    booking: {
      type: Object as () => BookingSummary,
      required: true,
    },
    isPastStatus: {
      type: Boolean,
      required: true,
    },
  })

  // Internationalization setup
  const { t } = useI18n()

  // Utility function to format dates to dd/MM/yyyy
  const formatDate = (date: string): string => {
    const parsedDate = new Date(date)
    const day = parsedDate.getDate().toString().padStart(2, '0')
    const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0')
    const year = parsedDate.getFullYear()
    return `${day}/${month}/${year}`
  }

  // Computed property for generating product URL
  const productUrl: ComputedRef<string> = computed((): string => {
    const getProductUrl = store.getters['pagemeta/getProductUrl']
    return getProductUrl(props.booking.liheId) || ''
  })

  // Computed property for generating popup link
  const popupLink: ComputedRef<string> = computed((): string => {
    if (!productUrl.value) return ''

    const { packageCode, options } = props.booking

    // Remove _****** from packageCode
    const cleanPackageCode = packageCode.split('_')[0]

    const prestationIds = options
      .filter(option => option.prestTypeId === PrestType.Accomodation)
      .map(option => option.prestId)

    const formattedPrestations = encodeURIComponent(
      JSON.stringify(prestationIds)
    )

    // Get dates from the accommodation option (prestTypeId: 1)
    const accommodationOption = options.find(option => option.prestTypeId === PrestType.Accomodation)
    const formattedBeginDate = formatDate(accommodationOption?.startDate || '')
    const formattedEndDate = formatDate(accommodationOption?.endDate || '')

    return `${productUrl.value}#season=H&packCode=${cleanPackageCode}&BeginDate=${formattedBeginDate}&EndDate=${formattedEndDate}&activePrestations=${formattedPrestations}`
  }) // SEASON HARDCODED!!!!

  // Method to open the product URL
  const handleOpenProduct = (): void => {
    if (productUrl.value) {
      console.log('Redirecting to product page:', productUrl.value)
      handleRedirect(productUrl.value)
    }
  }

  // Method to handle the popup link
  const handlePopupLink = (): void => {
    if (popupLink.value) {
      console.log('Redirecting to popup link:', popupLink.value)
      handleRedirect(popupLink.value)
    }
  }

  // Fetch product URL on component mount
  onMounted(async (): Promise<void> => {
    try {
      await store.dispatch('pagemeta/fetchProductURL', {
        liheId: props.booking.liheId,
      })
    } catch (error) {
      console.error('Error fetching product URL:', error)
    }
  })
</script>

<style scoped>
  /**
 * Scoped styles for the Panel Action component
 */
  .panel-action {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    height: 100%;
    width: 100%;
  }

  .link-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
